// @ts-nocheck

class PdpPolicyDialog extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLElement[]} */
  #triggers = [];

  /** @type {HTMLElement | null} */
  #activeTrigger = null;

  /** @type {Set<string>} */
  #loadedPanels = new Set();

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#triggers = this.#getTriggers();

    if (this.#triggers.length === 0) return;

    const closeBtn = this.#dialog.querySelector('[data-close-page-dialog], [data-close-policy-dialog]');

    this.#triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.#open);
    });
    closeBtn?.addEventListener('click', this.#close);
    this.#dialog.addEventListener('close', this.#handleClose);
    this.#dialog.querySelectorAll('[data-page-dialog-tab], [data-policy-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        const tabName = tab.dataset.pageDialogTab || tab.dataset.policyTab;
        this.#setActivePanel(tabName);
      });
    });
  }

  disconnectedCallback() {
    this.#triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.#open);
    });
    this.#triggers = [];
    this.#dialog?.removeEventListener('close', this.#handleClose);
  }

  #open = (/** @type {Event | undefined} */ event) => {
    if (!this.#dialog || this.#dialog.open) return;

    if (event?.currentTarget instanceof HTMLElement) {
      this.#activeTrigger = event.currentTarget;
    }

    this.#dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');

    const firstTab = this.#dialog.querySelector('[data-page-dialog-tab], [data-policy-tab]');
    if (firstTab instanceof HTMLElement) {
      this.#setActivePanel(firstTab.dataset.pageDialogTab || firstTab.dataset.policyTab);
      return;
    }

    // Single-page mode has no tabs, so load the first panel directly.
    const firstPanel = this.#dialog.querySelector('[data-page-dialog-panel], [data-policy-panel]');
    if (firstPanel instanceof HTMLElement) {
      const panelName = firstPanel.dataset.pageDialogPanel || firstPanel.dataset.policyPanel;
      if (panelName) {
        this.#setActivePanel(panelName);
      }
    }
  };

  #close = () => {
    this.#dialog?.close();
  };

  #handleClose = () => {
    document.documentElement.removeAttribute('scroll-lock');
    try {
      (this.#activeTrigger || this.#triggers[0])?.focus();
    } catch (_e) {}
  };

  #getTriggers() {
    if (!this.#dialog) return [];

    const internalTriggers = Array.from(this.querySelectorAll('[data-open-page-dialog], [data-open-policy-dialog]'));
    if (internalTriggers.length > 0) return internalTriggers;

    const dialogId = this.#dialog.id;
    if (!dialogId) return [];

    return Array.from(
      document.querySelectorAll(`[data-open-page-dialog="${dialogId}"], [data-open-policy-dialog="${dialogId}"]`),
    );
  }

  /**
   * @param {string | undefined} name
   */
  #setActivePanel(name) {
    if (!this.#dialog || !name) return;

    this.#dialog.querySelectorAll('[data-page-dialog-tab], [data-policy-tab]').forEach((tab) => {
      const tabName = tab.dataset.pageDialogTab || tab.dataset.policyTab;
      const active = tabName === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.#dialog.querySelectorAll('[data-page-dialog-panel], [data-policy-panel]').forEach((panel) => {
      const panelName = panel.dataset.pageDialogPanel || panel.dataset.policyPanel;
      panel.hidden = panelName !== name;
    });

    const activePanel =
      this.#dialog.querySelector(`[data-page-dialog-panel="${name}"]`) ||
      this.#dialog.querySelector(`[data-policy-panel="${name}"]`);
    if (activePanel instanceof HTMLElement && !this.#loadedPanels.has(name)) {
      this.#loadPanelContent(activePanel, name);
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} name
   */
  async #loadPanelContent(panel, name) {
    const pageUrl = panel.dataset.pageUrl;
    if (!pageUrl) return;

    const contentEl = panel.querySelector('.page-content-dialog__panel-content, .pdp-policy-dialog__panel-content');
    if (!contentEl) return;

    this.#loadedPanels.add(name);

    const loadingEl = document.createElement('div');
    loadingEl.className = 'pdp-policy-dialog__panel-loading';
    loadingEl.setAttribute('aria-hidden', 'true');
    // 1 block + 4 text lines
    for (let i = 0; i < 5; i++) {
      loadingEl.appendChild(document.createElement('span'));
    }
    contentEl.after(loadingEl);
    this.#dialog?.classList.add('is-loading');

    try {
      const url = new URL(pageUrl, window.location.origin);
      const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const main = doc.querySelector('main');
      const nativeSectionsMount = panel.querySelector('[data-policy-native-sections]');
      const hasFallbackPageContent = this.#hasRenderableContent(panel, '[data-policy-page-content]');
      const hasFallbackBlockContent = this.#hasRenderableContent(panel, '[data-policy-block-content]');

      // Keep Liquid-rendered page + block content intact and only inject fetched native sections.
      if (main && nativeSectionsMount instanceof HTMLElement) {
        const nativeSections = this.#extractNativeSections(main, {
          excludeMainSection: hasFallbackPageContent || hasFallbackBlockContent,
        });
        if (nativeSections.length > 0) {
          nativeSectionsMount.replaceChildren(...nativeSections);
          nativeSectionsMount.hidden = false;
        }
      }
    } catch (_err) {
      // On error, existing Liquid-rendered content remains
      this.#loadedPanels.delete(name);
    } finally {
      loadingEl.remove();
      this.#dialog?.classList.remove('is-loading');
    }
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} selector
   */
  #hasRenderableContent(panel, selector) {
    const target = panel.querySelector(selector);
    return Boolean(target && target.textContent && target.textContent.trim().length > 0);
  }

  /**
   * @param {Element} main
   * @param {{ excludeMainSection?: boolean }} [options]
   */
  #extractNativeSections(main, options = {}) {
    const candidates = Array.from(main.querySelectorAll('.shopify-section, [id^="shopify-section-"]'));
    const topLevelSections = candidates.filter((node) => {
      let parent = node.parentElement;
      while (parent && parent !== main) {
        if (parent.matches('.shopify-section, [id^="shopify-section-"]')) {
          return false;
        }
        parent = parent.parentElement;
      }
      return true;
    });

    const filteredSections = topLevelSections.filter((node) => {
      const id = node.id || '';
      const classList = node.className || '';
      const isMainTemplateSection = options.excludeMainSection && /__main/.test(id + ' ' + classList);
      if (isMainTemplateSection) return false;

      // Prevent recursive modal markup from being re-injected into this dialog.
      const hasPolicyDialogMarkup = node.querySelector(
        'pdp-policy-dialog, page-content-dialog, [data-policy-mount], [data-page-dialog-mount]',
      );
      if (hasPolicyDialogMarkup) return false;

      return true;
    });

    return filteredSections.map((node) => node.cloneNode(true));
  }
}

if (!customElements.get('pdp-policy-dialog')) {
  customElements.define('pdp-policy-dialog', PdpPolicyDialog);
}

if (!customElements.get('page-content-dialog')) {
  class PageContentDialog extends PdpPolicyDialog {}

  customElements.define('page-content-dialog', PageContentDialog);
}
